// TGCLOUD-1669 — how the PERSON form packs its fields.
//
// The rule, from the 2026-09-06 plan amendment: ONE form, the container opts
// in, and a minimum column width decides how many columns actually fit. Not
// two layouts to keep in step, and not a reading surface with a pencil. Every
// field stays a live, immediately editable control at every width. The only
// thing that changes is how many sit on a row.
//
// WHY THIS IS SAFE HERE AND WAS NOT IN THE DRAWER. The promoted drawer's
// content column is about 464px with the record dock open, so a three column
// grid there is about 155px a cell, which cannot hold a TextField and its
// label. That is the whole reason the withdrawn version made cells read-only
// text. The person tab's centre column is capped at 760px, so two columns is
// about 370px each, which is comfortable for a real input.
//
// `auto-fill` with a `minmax` floor, deliberately, rather than a container
// query. It needs no at-rule, so the browser resolves the column count from
// the width the form actually has, and the declaration sits on the base rule
// where a test can read it. jsdom applies NOTHING inside an at-rule, so a
// container query here would have been unverifiable by any test in this
// repository, which is precisely how the withdrawn round shipped a layout that
// passed the whole suite and failed at first look.

/**
 * How much room a field needs. Two values, not the four the amendment sketched.
 *
 * At a 280px floor inside a 760px column there are exactly two tracks, so
 * "wide" and "full-span" are the same row and "narrow" and "medium" both
 * occupy one track. Naming four things that resolve to two would be a
 * distinction nothing acts on.
 */
export const FIELD_WIDTH = Object.freeze({
  /** One track. The default, and what most fields are. */
  standard: 'standard',
  /**
   * A whole row. For anything whose content is unbounded or long enough that a
   * single track truncates it: a chip array, a native select carrying a
   * 63-character option, a free-text note, a warning banner, an embedded card.
   */
  wide: 'wide'
});

/**
 * The narrowest a column may be before the grid drops one.
 *
 * 280px is a floor with a specific job. The date of birth and date of death
 * controls break FUNCTIONALLY below about 270px: once a user ticks "Exact Date
 * Unknown", PartialDateInput renders three precision tabs in MUI's default
 * non-scrollable variant, and the third one, "Full date", is clipped out of
 * reach with no scroll affordance. Its "Month + year" row needs a 140px select
 * beside a usable year box as well. Neither is visible in a screenshot of the
 * default state, so this floor is load-bearing rather than aesthetic.
 */
export const MIN_COLUMN_PX = 280;

/**
 * The form container.
 *
 * @param {boolean} wide  whether the container has opted in. The person tab
 *   does; the drawer does not, so the 400px peek and the promoted drawer are
 *   untouched and stay one field per row.
 */
export const formGridSx = (wide, spacing = 2) => ({
  display: 'grid',
  columnGap: 2,
  // The DRAWER keeps the exact vertical rhythm it had before this existed.
  // Each call site passes the MUI `spacing` its Grid container used, because
  // the blocks did not all use the same one: the form was 2, the name and date
  // groups 1, contact 2, spouse 3. Collapsing them all to one value halved the
  // form's spacing in the peek, and the plan requires the drawer to be
  // untouched for a clinician who never promotes.
  //
  // The wide grid is tighter on purpose: rows sit beside each other there, so
  // the same gap reads as loose.
  rowGap: wide ? 1 : spacing,
  alignItems: 'start',
  gridTemplateColumns: wide
    ? `repeat(auto-fill, minmax(${MIN_COLUMN_PX}px, 1fr))`
    : '1fr'
});

/**
 * One field's placement.
 *
 * A `wide` field takes the whole row rather than a fixed number of tracks:
 * `span 2` against an auto-fill grid that has resolved to ONE column creates an
 * implicit second column and overflows the form, which is the trap this avoids.
 * `1 / -1` is correct at any column count.
 */
export const fieldSx = (width = FIELD_WIDTH.standard) => ({
  minWidth: 0,
  ...(width === FIELD_WIDTH.wide ? { gridColumn: '1 / -1' } : null)
});
